<script setup>
import autoAnimate from '@formkit/auto-animate'
import { createReusableTemplate } from '@vueuse/core'
import { cloneDeep, isEmpty, isEqual } from 'lodash'
import { Portal } from 'portal-vue'
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useStore } from 'vuex'

import ElementProgressApiHandler from '@/api/element-progress'
import { BUTTON_SIZES } from '@/utils/components-configurations/app-button'
import {
  ELEMENT_PROGRESS_TABLE_COLUMN_COUNT_OPTIONS,
  useViewPointLastDate
} from '@/utils/element-progress'
import { ELEMENT_PROGRESS_ENTITY_KEYS, TARGET_PERIOD_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { DEFAULT_MILESTONES_SETTINGS } from '@/utils/okr-element-values'
import { ELEMENT_PROGRESS_TABLE_COLUMN_COUNT } from '@/utils/user-settings'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

import {
  ELEMENT_PROGRESS_MOVE_BACKWARD_BUTTON_TEST_ID,
  ELEMENT_PROGRESS_MOVE_FORWARD_BUTTON_TEST_ID
} from '@/components/check-ins/jest-helpers'
import ElementProgressTableColumn from '@/components/form/element-progress-table/ElementProgressTableColumn'
import ElementProgressTableLoader from '@/components/form/element-progress-table/ElementProgressTableLoader'
import {
  ELEMENT_PROGRESS_TABLE_COLUMN_COUNT_SWITCH_TEST_ID,
  ELEMENT_PROGRESS_TABLE_TEST_ID
} from '@/components/form/jest-helpers'
import ProgressViewColumnsSelect from '@/components/objectives/progress-view/ProgressViewColumnsSelect'
import AppButton from '@/components/ui/AppButton/AppButton'

defineOptions({
  name: 'ElementProgressTable',
  inheritAttrs: false
})

const props = defineProps({
  elementId: {
    type: [String, Number],
    required: true
  },

  element: {
    type: Object,
    required: true
  },

  elementTargetPeriods: {
    type: Array,
    default: () => DEFAULT_MILESTONES_SETTINGS[OKR_ELEMENT_KEYS.ELEMENT_TARGET_PERIODS]
  },

  portalName: {
    type: String,
    default: ''
  }
})

const bufferPrev = ref([])

const bufferNext = ref([])

const tableData = ref([])

const BUFFER_ELEMENTS_COUNT = 1

const onUpdateColumnCount = async () => {
  await getTableData({ forceUpdate: true, clearCurrentData: true })
}

const store = useStore()

const savedColumnCount = computed(() => {
  return store.getters['system/savedColumnCount']
})

const { viewPointLastDate } = useViewPointLastDate({
  [ELEMENT_PROGRESS_ENTITY_KEYS.PERIODS]: tableData
})

const getChunkedData = ({ periods = [], progressData = [], forceUpdate = false } = {}) => {
  if (isEmpty(periods) || isEmpty(progressData)) return

  const getBufferElementsCount = ({ forceUpdate = false, buffer = [] } = {}) => {
    return forceUpdate && !isEmpty(tableData.value) ? buffer.length : BUFFER_ELEMENTS_COUNT
  }

  const prevBufferCount = getBufferElementsCount({ forceUpdate, buffer: bufferPrev.value })
  // const nextBufferCount = getBufferElementsCount({ forceUpdate, buffer: bufferNext.value })

  const createChunks = (arr = []) => {
    /**
     * @ATTENTION
     * DO NOT SIMPLIFY arr.splice(-nextBufferCount, nextBufferCount)` to `arr.slice(-nextBufferCount)`
     * because in case of count === 0 `arr.slice(-0)` will interpret as `arr.slice(0, Infinity)`
     * and return the whole array instead of an empty array
     * @ATTENTION
     */
    // return [arr.splice(0, prevBufferCount), arr.splice(-nextBufferCount, nextBufferCount), arr]
    return [arr.splice(0, prevBufferCount), arr.splice(0, savedColumnCount.value), arr]
  }

  const [prevPeriods, mainPeriods, nextPeriods] = createChunks(periods)
  const [prevProgress, mainProgress, nextProgress] = createChunks(progressData)

  const concatData = (periods = [], progress = []) => {
    return periods.map((period, index) => ({
      period,
      progress: progress[index] || {}
    }))
  }

  return {
    chunkPrev: cloneDeep(concatData(prevPeriods, prevProgress)),
    chunkNext: cloneDeep(concatData(nextPeriods, nextProgress)),
    chunkMain: cloneDeep(concatData(mainPeriods, mainProgress))
  }
}

const isLoading = ref(false)

const isCellLoading = ref(false)

const getTableData = async ({
  forceUpdate = false,
  resetToInitialState = false,
  clearCurrentData = false
} = {}) => {
  const api = new ElementProgressApiHandler()

  isLoading.value = true

  if (resetToInitialState || clearCurrentData) {
    bufferPrev.value = []
    bufferNext.value = []
    tableData.value = []
  }

  try {
    const { periods, progressData } = await api.getElementProgress({
      elementId: props.elementId,
      viewPointLastDate: resetToInitialState ? undefined : viewPointLastDate.value,
      columnCount: savedColumnCount.value,
      shiftSize: BUFFER_ELEMENTS_COUNT
    })

    const { chunkMain, chunkNext, chunkPrev } = getChunkedData({
      [ELEMENT_PROGRESS_ENTITY_KEYS.PERIODS]: cloneDeep(periods),
      progressData,
      forceUpdate
    })

    const currentData = [...bufferPrev.value, ...tableData.value, ...bufferNext.value].map(
      ({ period }) => period[TARGET_PERIOD_ENTITY_KEYS.START_DATE]
    )

    const newData = periods.map(({ startDate }) => startDate)

    const isDataChanged = !isEqual(currentData, newData)

    if (forceUpdate || isDataChanged) {
      bufferPrev.value = chunkPrev
      bufferNext.value = chunkNext
      tableData.value = chunkMain
    }
  } catch (error) {
    handleError({ error })
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  getTableData()
})

const ANIMATION_NAMES = {
  FORWARD: 'table-forward',
  BACKWARD: 'table-backward'
}

const animationName = ref('')

const moveForward = async () => {
  if (!isEmpty(bufferNext.value)) {
    animationName.value = ANIMATION_NAMES.FORWARD
    bufferPrev.value.push(tableData.value.shift())
    tableData.value.push(bufferNext.value.shift())

    await getTableData()
  }
}

const moveBackward = async () => {
  if (!isEmpty(bufferPrev.value)) {
    animationName.value = ANIMATION_NAMES.BACKWARD
    bufferNext.value.unshift(tableData.value.pop())
    tableData.value.unshift(bufferPrev.value.pop())

    await getTableData()
  }
}

const tableWrapperReference = useTemplateRef('table-wrapper')

const ANIMATION_ACTIONS = {
  ADD: 'add',
  REMOVE: 'remove',
  REMAIN: 'remain'
}

watch(tableWrapperReference, async (newValue, oldValue) => {
  if (newValue && !oldValue) {
    await nextTick()

    autoAnimate(newValue, (el, action, oldCoords, newCoords) => {
      let keyframes

      const createKeyframes = ({ from = 0, to = 0 } = {}) => [
        { transform: `translateX(${from})` },
        { transform: `translateX(${to})` }
      ]

      if (action === ANIMATION_ACTIONS.ADD) {
        const multiplier = animationName.value === ANIMATION_NAMES.FORWARD ? 1 : -1
        const from = `${multiplier * 100}%`

        keyframes = createKeyframes({ from })
      }

      if (action === ANIMATION_ACTIONS.REMOVE) {
        const multiplier = animationName.value === ANIMATION_NAMES.BACKWARD ? 1 : -1

        const to = `${multiplier * 100}%`

        keyframes = createKeyframes({
          to
        })
      }

      if (action === ANIMATION_ACTIONS.REMAIN) {
        const from = `${oldCoords.left - newCoords.left}px`

        keyframes = createKeyframes({ from })
      }

      return new KeyframeEffect(el, keyframes, { duration: 80, easing: 'ease-in-out' })
    })
  }
})

/*const getNavRange = computed(() => {
  if (isEmpty(tableData.value)) return ''

  const {
    period: { name: firstCellName }
  } = tableData.value.at(0)

  const {
    period: { name: lastCellName }
  } = tableData.value.at(-1)

  return `${firstCellName} ${DATES_RANGE_SEPARATOR} ${lastCellName}`
})*/

const emit = defineEmits({
  'value-updated': null,
  'threshold-updated': null,
  'open-checkin-modal': null
})

const onValueUpdated = event => {
  emit('value-updated', event)
}

const onThresholdUpdated = event => {
  emit('threshold-updated', event)
}

const onOpenCheckinModal = event => {
  emit('open-checkin-modal', event)
}

const forceUpdateTable = () => {
  getTableData({ forceUpdate: true })
}

defineExpose({
  updateTableData: forceUpdateTable
})

const isNavButtonDisabled = computed(() => {
  return isLoading.value || isCellLoading.value
})

const [DefineTemplate, ReuseTemplate] = createReusableTemplate()
</script>

<template>
  <div class="ept-Wrapper">
    <DefineTemplate>
      <AppButton
        :class="{
          'ept-NavButton-disabled': isNavButtonDisabled
        }"
        :disable="isNavButtonDisabled"
        :size="BUTTON_SIZES.SM"
        :type="isNavButtonDisabled ? 'primary-next' : 'ghost-next'"
        class="ept-NavButton"
        remove-padding
      />
    </DefineTemplate>
    <template v-if="!isEmpty(tableData)">
      <portal :disabled="!portalName" :to="portalName">
        <ProgressViewColumnsSelect
          :count="savedColumnCount"
          :data-auto-testid="ELEMENT_PROGRESS_TABLE_COLUMN_COUNT_SWITCH_TEST_ID"
          :data-testid="ELEMENT_PROGRESS_TABLE_COLUMN_COUNT_SWITCH_TEST_ID"
          :disabled="isLoading || isCellLoading"
          :options="ELEMENT_PROGRESS_TABLE_COLUMN_COUNT_OPTIONS"
          :setting-key="ELEMENT_PROGRESS_TABLE_COLUMN_COUNT"
          @update-count="onUpdateColumnCount"
        />
      </portal>
      <div class="ept-Content">
        <ReuseTemplate
          :data-auto-testid="ELEMENT_PROGRESS_MOVE_BACKWARD_BUTTON_TEST_ID"
          :data-testid="ELEMENT_PROGRESS_MOVE_BACKWARD_BUTTON_TEST_ID"
          icon="chevron-left-next"
          @click="moveBackward"
        />

        <div
          ref="table-wrapper"
          :data-auto-testid="ELEMENT_PROGRESS_TABLE_TEST_ID"
          :data-testid="ELEMENT_PROGRESS_TABLE_TEST_ID"
          class="ept-Table"
        >
          <ElementProgressTableColumn
            v-for="column in tableData"
            :key="column.period[TARGET_PERIOD_ENTITY_KEYS.START_DATE]"
            :data="column.progress.data"
            :element="element"
            :element-target-periods="elementTargetPeriods"
            :header-data="column.period"
            :progress="column.progress"
            @loading="isCellLoading = $event"
            @value-updated="onValueUpdated"
            @threshold-updated="onThresholdUpdated"
            @open-checkin-modal="onOpenCheckinModal"
          />
        </div>

        <ReuseTemplate
          :data-auto-testid="ELEMENT_PROGRESS_MOVE_FORWARD_BUTTON_TEST_ID"
          :data-testid="ELEMENT_PROGRESS_MOVE_FORWARD_BUTTON_TEST_ID"
          icon="chevron-right-next"
          @click="moveForward"
        />
      </div>
    </template>

    <ElementProgressTableLoader v-else :column-count="savedColumnCount" />
  </div>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/mixins';

.ept-Table {
  flex: 1;
  // margin-top: 20px;
  display: flex;
  gap: 1px;
  background-color: $grey-3-next;
  // border-radius: $border-radius-sm-next;
  overflow: hidden;
}

.ept-Content {
  display: flex;
  background-color: $grey-3-next;
  gap: 1px;
}

.ept-NavButton {
  flex-shrink: 0;
  --border-radius: 0;
  height: auto;
  --ob-ab-ghost-next-background-color: #{$white};

  &-disabled {
    background-color: var(--ob-ab-ghost-next-background-color);
  }
}

.ept-Wrapper {
  // margin-top: 20px;
  // padding-bottom: 4px;
  overflow: hidden;
  // width: calc(100% - var(--compensate-chart-outer-offset, 0px));
  width: 100%;
  --divider-offset: 0 0 20px 0;
  --divider-color: #{$grey-3-next};
  --divider-border-radius: #{$border-radius-xs};
}

/*.ept-Nav_Range {
  height: 100%;
  padding: 8px;
  color: $dark-2;
  font-size: $fs-12;
  line-height: 16px;
  font-weight: fw('bold');
  background-color: $grey-3-next;
  border-radius: $border-radius-sm-next;
}*/
</style>
